import styled from 'styled-components/macro';
import scaleSize from 'utils/scale-size';
import config from 'config';
import api from 'config/api';
import BackButton from 'components/BackButton';
import { Focusable, FocusableParent } from 'components/navigation';
import getImagePath from 'utils/get-image-path';
import HorizontalList from 'components/HorizontalList';

const SLOW_TRANSITION = config.mainTransitionDuration;
const FAST_TRANSITION = '0.3s';

export const DetailsWrapper = styled.div`
  padding-left: ${scaleSize(220)}px;
  height: ${scaleSize(1080)}px;
`;

export const Header = styled.div.attrs(({ imageUrl }) => ({
  style: {
    background: imageUrl
      ? `center ${scaleSize(-90)}px url(${api.host}${imageUrl}?width=${scaleSize(1920)}&height=${scaleSize(
          560,
        )}) no-repeat`
      : '',
  },
}))`
  width: ${scaleSize(1920)}px;
  height: auto;
  margin-left: -${scaleSize(220)}px;
  position: relative;

  &:before {
    position: absolute;
    z-index: -1;
    top: 0;
    left: 0;
    width: 100%;
    height: ${scaleSize(450)}px;
    content: '';
    background: ${({ theme }) => theme.imagePlaceholder} linear-gradient(to top, rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0));
  }
`;

export const StyledImageOverlay = styled.div`
  position: absolute;
  top: 0;
  width: ${scaleSize(1920)}px;
  height: ${scaleSize(452)}px;
  background: linear-gradient(360deg, ${({ theme }) => theme.backgroundColor} 0.09%, rgba(38, 38, 38, 0.0001) 100%);
`;

export const DetailsInnerWrapper = styled.div`
  width: ${scaleSize(1920)}px;
  transform: translate3d(0, ${({ pageOffset }) => scaleSize(pageOffset)}px, 0);
  transition: ${({ noAnimation }) => (config.withAnimation && !noAnimation ? `transform ${SLOW_TRANSITION}` : 'none')};
  position: relative;
`;
export const StyledBackButton = styled(BackButton)`
  z-index: 4;
  left: -${scaleSize(186)}px;
  color: ${({ theme, buttonOffset }) => (buttonOffset > 540 ? theme.activeColor : theme.detailsTitleTextColor)};
  transform: translate3d(0, ${({ buttonOffset }) => scaleSize(buttonOffset)}px, 0);
  transition: ${({ noAnimation }) =>
    config.withAnimation && !noAnimation ? `transform ${SLOW_TRANSITION}, color ${SLOW_TRANSITION}` : 'none'};
`;

export const StyledAvailableStatus = styled.div`
  position: absolute;
  top: ${scaleSize(650)}px;
  width: ${scaleSize(432)}px;
  font-size: ${scaleSize(34)}px;
  line-height: ${scaleSize(48)}px;
  font-family: 'RobotoMedium', sans-serif;
  font-weight: 500;
  text-align: center;
  color: ${({ theme }) => theme.activeColor};
`;

export const CoverWrapper = styled(FocusableParent)`
  width: ${scaleSize(432)}px;
  height: ${scaleSize(640)}px;
  position: absolute;
  z-index: 2;
  top: ${scaleSize(220)}px;
  right: ${scaleSize(340)}px;
  opacity: ${({ opacity }) => opacity};
  transform: translate3d(0, ${({ coverOffset }) => scaleSize(coverOffset)}px, 0);
  transition: ${({ noAnimation }) => (config.withAnimation && !noAnimation ? `transform ${SLOW_TRANSITION}` : 'none')};
`;

export const Cover = styled(Focusable)`
  display: inline-block;
  width: ${scaleSize(432)}px;
  height: ${scaleSize(640)}px;
  position: relative;
  box-shadow: 0 15px 40px 0 rgba(0, 0, 0, 0.15);
  background-color: ${({ theme }) => theme.mainTextColor};
  background-image: url(${({ cover }) => getImagePath(cover, scaleSize(432), scaleSize(640))});
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  box-sizing: border-box;
  border-radius: ${scaleSize(16)}px;

  > svg {
    position: absolute;
    bottom: ${scaleSize(20)}px;
    right: ${scaleSize(20)}px;
    opacity: 0;
    width: ${scaleSize(100)}px;
    height: ${scaleSize(100)}px;
  }

  &:after {
    content: '';
    display: block;
    width: ${scaleSize(432 - 15)}px;
    height: ${scaleSize(630)}px;
    position: absolute;
    top: 0;
    border: solid ${scaleSize(8)}px transparent;
    border-radius: ${scaleSize(16)}px;
  }

  &:focus {
    &:after {
      border-color: ${({ theme }) => theme.activeColor};
    }
    > svg {
      opacity: 1;
    }
  }
`;

export const ActionButtonsWrapper = styled(FocusableParent)`
  width: ${scaleSize(1048)}px;
  height: ${({ isVisible }) => scaleSize(isVisible ? 80 : 0)}px;
  margin-top: ${({ isVisible }) => (isVisible ? scaleSize(35) : 0)}px;
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  transition: ${config.withAnimation ? `opacity ${FAST_TRANSITION}` : 'none'};
  transition-delay: ${({ isVisible }) => (isVisible ? FAST_TRANSITION : '0s')};
`;

export const ExpandTextWrapper = styled(FocusableParent)`
  width: ${scaleSize(1048)}px;
  height: ${scaleSize(80)}px;
  margin-top: ${scaleSize(60)}px;
`;

export const DescriptionWrapper = styled.div`
  width: ${scaleSize(1048)}px;
  margin-top: ${scaleSize(60)}px;
  color: ${({ theme }) => theme.secondaryTextColor};
  font-size: ${scaleSize(34)}px;
  line-height: ${scaleSize(48)}px;
  font-family: 'RobotoRegular', sans-serif;
`;

export const DescriptionCastWrapper = styled.div`
  height: ${({ useFixedHeight }) => (useFixedHeight ? `${scaleSize(470)}px` : 'auto')};
`;

export const CastWrapper = styled.div`
  width: ${scaleSize(1048)}px;
  margin-top: ${scaleSize(40)}px;
`;

export const CastTitle = styled.div`
  width: ${scaleSize(160)}px;
  display: inline-block;
  color: ${({ theme }) => theme.secondaryTextColor};
  font-size: ${scaleSize(34)}px;
  line-height: ${scaleSize(48)}px;
  vertical-align: top;
`;

export const CastList = styled.div`
  width: ${scaleSize(888)}px;
  display: inline-block;
  color: ${({ theme }) => theme.mainTextColor};
  font-size: ${scaleSize(34)}px;
  line-height: ${scaleSize(48)}px;
`;

export const SeasonsWrapper = styled.div`
  width: ${scaleSize(1480)}px;
  margin-top: ${scaleSize(100)}px;
`;

export const SeasonsTabs = styled(HorizontalList)`
  width: ${scaleSize(1480)}px;
  margin-bottom: ${scaleSize(40)}px;
  border-bottom: solid 2px #444444;
  white-space: nowrap;
  font-size: 0;
`;

export const SeasonsTab = styled(Focusable)`
  font-family: RobotoMedium, sans-serif;
  font-size: ${scaleSize(30)}px;
  line-height: ${scaleSize(80)}px;
  text-align: center;
  text-overflow: ellipsis;
  overflow: hidden;
  padding: 0 ${scaleSize(30)}px;
  color: #cccccc;
  display: inline-block;
  border-top-left-radius: ${scaleSize(12)}px;
  border-top-right-radius: ${scaleSize(12)}px;
  background: ${({ isSelected }) => (isSelected ? '#444444' : 'transparent')};
  width: ${({ itemWidth }) => (itemWidth ? `${itemWidth}px` : 'auto')};

  &:focus {
    background: ${({ theme }) => theme.activeColor};
    color: #fff;
  }
`;

export const StyledEmptySeasonStub = styled.div`
  height: ${scaleSize(512)}px;
  font-size: ${scaleSize(48)}px;
`;

export const StyledEmptySeasonStubTitle = styled.div`
  position: relative;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.mainTextColor};
  text-align: center;
`;
